/**
 * The workbench's neutral zone.
 *
 * The toon material is anchored to a zone (`applyZone`) and to an exposure
 * reference (`setExposureReference`). Both are normally set by
 * `createZoneLighting`, which the asset workbench deliberately does not use: the
 * workbench exists to judge SILHOUETTE and PROPORTION, and reviewing a mesh
 * under the fattening hall's amber sodium haze would hide exactly the faults the
 * G1 gate is for.
 *
 * So the workbench gets its own zone: neutral, slightly warm, and with a chroma
 * ceiling of 1 because nothing here is graded. It is not a zone of the game and
 * must never be added to `ZONES` — it is a light meter.
 */

import type { ZonePalette } from '@/render/palette';

export const STUDIO_ZONE: ZonePalette = {
  id: 'studio',
  label: 'Workbench studio',
  brief: 'neutral review lighting — not a place in the game',
  key: 0xfff4e8,
  fill: 0x7e858e,
  shadow: 0x2f343b,
  fog: 0x2a2e34,
  surfaces: [0x2f343b, 0x6a7078, 0x9aa1a9, 0xc9cfd6, 0xfff4e8],
  chromaCeiling: 1,
  haze: 0,
};

/**
 * The irradiance that counts as fully lit under `studioLighting()`.
 *
 * Measured, not derived. The studio is a four-light rig — key 2.2, fill 0.7,
 * rim 1.1, hemisphere 1.1 — and a front-facing surface catches most of them at
 * once, so the peak is well above the key's own intensity.
 *
 * The first value here was 3.0, which is roughly what the key plus half the
 * hemisphere delivers, and it was wrong in a way worth recording: at 3.0 nearly
 * every surface on Rosalie sat above the ramp's 0.62 threshold, so she rendered
 * as ONE flat band of coral with no form at all. It looked like the toon shader
 * had failed. Nothing was broken — the anchor was simply below the range of
 * light the rig actually produces, and everything clipped to the top.
 *
 * If the workbench ever goes flat again, this number is the first suspect and
 * the lighting is the second.
 */
export const STUDIO_EXPOSURE = 4.2;
